import { useEffect, useState } from 'react';
import { useMatch } from 'react-router';
import { NewTabLink } from '@gonfalon/launchpad-experimental/NewTabLink';

import { clearAgentControlSignupPostAuthDestination } from '#utils/signupPostAuthDestinationStorage';
import {
  SIGNUP_TAB_ENTERPRISE_DEMO,
  SIGNUP_TAB_TRIAL,
  handleSignupIntentTabChange,
} from '#utils/tracking/signupEnterpriseDemo';

import FlagControlledSignUpBannerContainer from './FlagControlledSignUpBannerContainer';
import { OAuthButtons } from './OAuthButtons';
import { SignUpForm } from './SignUpForm';
import { SignUpIntentTabs } from './SignUpIntentTabs';
import { useCaptureSignupLandingUrl } from './useCaptureSignupLandingUrl';

import styles from './SignUpMain.module.css';

type SignUpMainProps = {
  headingClassName?: string;
  /** When false (default), clears Agent Control post-signup redirect if user switches to a generic signup flow. */
  agentControl?: boolean;
  /** Google OAuth pre-auth navigates here so session state (UTMs, post-signup dest.) stays in sync. */
  oauthRedirectPath?: string;
};

export const SignUpMain = ({
  headingClassName,
  agentControl = false,
  oauthRedirectPath = '/signup',
}: SignUpMainProps) => {
  const isSignupPage = useMatch({ path: '/signup', end: true });

  useCaptureSignupLandingUrl();

  useEffect(() => {
    if (!agentControl) {
      clearAgentControlSignupPostAuthDestination();
    }
  }, [agentControl]);

  const showEnterpriseDemoTabs = !agentControl;
  const [selectedIntentTab, setSelectedIntentTab] = useState(SIGNUP_TAB_TRIAL);
  const isEnterpriseDemoIntent = selectedIntentTab === SIGNUP_TAB_ENTERPRISE_DEMO;

  const handleIntentTabChange = (nextTab: string) => {
    handleSignupIntentTabChange(selectedIntentTab, nextTab);
    setSelectedIntentTab(nextTab);
  };

  return (
    <>
      <hgroup className={headingClassName}>
        {showEnterpriseDemoTabs && isEnterpriseDemoIntent ? (
          <>
            <h1>Evaluate Enterprise Solutions</h1>
            <p>
              Partner with our sales team to design a custom evaluation. We&rsquo;ll help you map out the impact of
              LaunchDarkly at scale.
            </p>
          </>
        ) : (
          <>
            <h1>Create your free account</h1>
            <p>
              Start with 14 days of full access, then continue on a free{' '}
              <NewTabLink
                className={styles.link}
                href="https://launchdarkly.com/pricing/"
                text="Developer account"
                showIcon={false}
              />{' '}
              with limits. No credit card required.
            </p>
          </>
        )}
      </hgroup>
      {isSignupPage && <FlagControlledSignUpBannerContainer />}
      {showEnterpriseDemoTabs ? (
        <SignUpIntentTabs
          selectedTab={selectedIntentTab}
          onSelectedTabChange={handleIntentTabChange}
          oauthRedirectPath={oauthRedirectPath}
        />
      ) : (
        <>
          <OAuthButtons oauthRedirectPath={oauthRedirectPath} />
          <section>
            <SignUpForm />
          </section>
        </>
      )}
      <div className={styles.signInDisclaimer}>
        <small>
          Already have an account? <a href="/login">Sign in</a>
        </small>
        <small>
          By using LaunchDarkly, you agree to the LaunchDarkly{' '}
          <NewTabLink href="https://launchdarkly.com/policies/subscription-terms/" text="Terms" showIcon={false} /> and{' '}
          <NewTabLink href="https://launchdarkly.com/policies/privacy/" text="Privacy Policies" showIcon={false} />.
        </small>
      </div>
    </>
  );
};
